class Modal {
  constructor({
    openSelector = '[data-drawer-open]',
    closeSelector = '[data-drawer-close]',
    backdropSelector = '.modal__backdrop',
    panelSelector = '.modal__panel',
    activeClass = 'active',
    bodyClass = 'modal-open',
    focusableSelectors = `
      a[href],
      button:not([disabled]),
      textarea:not([disabled]),
      input:not([disabled]),
      select:not([disabled]),
      [tabindex]:not([tabindex="-1"])
    `
  } = {}) {
    this.openSelector = openSelector;
    this.closeSelector = closeSelector;
    this.backdropSelector = backdropSelector;
    this.panelSelector = panelSelector;
    this.activeClass = activeClass;
    this.bodyClass = bodyClass;
    this.focusableSelectors = focusableSelectors;

    this.init();
  }

  init() {
    if (window.__modalInitialized) return;

    window.__modalInitialized = true;

    document.addEventListener('DOMContentLoaded', () => {
      this.bindOpenButtons();
      this.bindCloseButtons();
      this.bindBackdrops();
      this.setupInertSync();
    });
  }

  // A closed modal is hidden with `opacity: 0` only, which leaves its links in
  // the keyboard tab order. Mirror the `inert` attribute onto each container so
  // closed modals drop out of the tab order / a11y tree and only the open one is
  // reachable. The observer keeps this in sync regardless of which controller
  // toggles `.active` (this class, or the desktop-nav panel controller).
  setupInertSync() {
    const reflectInert = (container) => {
      container.toggleAttribute(
        'inert',
        !container.classList.contains(this.activeClass)
      );
    };

    document.querySelectorAll('.modal__container').forEach((container) => {
      reflectInert(container);

      new MutationObserver(() => reflectInert(container)).observe(container, {
        attributes: true,
        attributeFilter: ['class'],
      });
    });
  }

  bindOpenButtons() {
    document.querySelectorAll(this.openSelector).forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-drawer-open');
        this.open(id, btn);
      });
    });
  }

  bindCloseButtons() {
    document.querySelectorAll(this.closeSelector).forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-drawer-close');
        this.close(id);
      });
    });
  }

  bindBackdrops() {
    document.querySelectorAll(this.backdropSelector).forEach((backdrop) => {
      backdrop.addEventListener('click', () => {
        const container = backdrop.closest('.modal__container');

        if (container) {
          this.close(container.id);
        }
      });
    });
  }

  open(id, triggerButton) {
    const drawer = document.getElementById(id);

    if (!drawer) return;

    // Portal the drawer to <body> so its `position: fixed` overlay escapes any
    // ancestor that forms a containing block (e.g. the header's `will-change`/
    // `transform`) and isn't capped by that ancestor's stacking context.
    if (drawer.parentElement !== document.body) {
      document.body.appendChild(drawer);
      // Commit the closed (off-screen) state in the drawer's new position before
      // adding `.active`, otherwise the browser collapses the move + class change
      // into one frame and skips the slide-in transition. (Mirrors details-modal.js.)
      void drawer.offsetWidth;
    }

    drawer.classList.add(this.activeClass);
    document.body.classList.add(this.bodyClass);

    // Drop `inert` synchronously before focusing: the observer in setupInertSync
    // reacts to the class change too, but async — and you can't focus inside an
    // inert element, so trapFocus' focus() would no-op without this.
    drawer.removeAttribute('inert');

    // Remember whichever trigger opened this drawer so close() can restore
    // focus and aria-expanded on it, even when closed via the backdrop, a
    // different close button inside the panel, or a later reopen by another
    // trigger sharing the same drawer id.
    if (drawer._opener && drawer._opener !== triggerButton) {
      drawer._opener.setAttribute('aria-expanded', 'false');
    }
    drawer._opener = triggerButton || null;
    if (triggerButton) {
      triggerButton.setAttribute('aria-expanded', 'true');
    }

    const panel = drawer.querySelector(this.panelSelector);

    if (panel) {
      this.trapFocus(panel);
    }
  }

  close(id) {
    const drawer = document.getElementById(id);

    if (!drawer) return;

    drawer.classList.remove(this.activeClass);
    document.body.classList.remove(this.bodyClass);

    const panel = drawer.querySelector(this.panelSelector);

    if (panel && panel._removeFocusTrap) {
      panel._removeFocusTrap();
    }

    if (drawer._opener) {
      drawer._opener.setAttribute('aria-expanded', 'false');
      if (drawer._opener.isConnected) drawer._opener.focus();
      drawer._opener = null;
    }
  }

  trapFocus(modal) {
    // querySelectorAll also returns hidden elements (e.g. the desktop-hidden
    // Back buttons, or closed sub-panels at opacity:0) — focusing one silently
    // fails and focus never enters the panel. Keep only visible elements, and
    // recompute on each Tab so the boundaries follow sub-panels drilling open.
    const isVisible = (el) =>
      typeof el.checkVisibility === 'function'
        ? el.checkVisibility({ visibilityProperty: true, opacityProperty: true })
        : el.getClientRects().length > 0;

    const getFocusable = () =>
      Array.from(modal.querySelectorAll(this.focusableSelectors)).filter(
        isVisible
      );

    const handleTab = (e) => {
      if (e.key !== 'Tab') return;

      const elements = getFocusable();

      if (!elements.length) return;

      const firstElement = elements[0];
      const lastElement = elements[elements.length - 1];

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    };

    // Attach the trap up front so focus is contained even before the initial
    // focus lands — it recomputes focusables on every Tab.
    modal.addEventListener('keydown', handleTab);

    modal._removeFocusTrap = () => {
      modal.removeEventListener('keydown', handleTab);
    };

    // The container fades in via an `opacity` transition, so at this synchronous
    // moment every descendant still computes as opacity:0 and fails the
    // visibility check — getFocusable() would be empty and focus would never
    // enter the panel. Wait two frames for the transition to begin, then place
    // the initial focus on the marked target (e.g. the close button), falling
    // back to the first focusable element.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        const focusable = getFocusable();

        if (!focusable.length) return;

        const initialFocus =
          Array.from(
            modal.querySelectorAll('[data-modal-initial-focus]')
          ).find(isVisible) || focusable[0];

        initialFocus.focus();
      })
    );
  }
}

new Modal();